import { IconPanelRightOpen } from "@/assets/icons/geist/IconPanelRightOpen";
import { withGitpodSize } from "@/assets/icons/utils";
import { AgentExecutionDropdownMenu } from "@/components/ai/AgentExecutionDropdownMenu";
import {
    ConversationContent,
    ConversationError,
    ConversationInput,
} from "@/components/ai/chat/AgentExecutionConversation";
import { PendingInteractiveBlock } from "@/components/ai/PendingInteractiveBlock";
import { extractPendingInteractiveBlock } from "@/components/ai/pending-interactive-utils";
import { Button } from "@/components/flexkit/Button";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useDetailsPanelToggleShortcut } from "@/hooks/use-details-panel-toggle-shortcut";
import type { FollowModeState } from "@/hooks/use-follow-mode";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useOptimisticPendingMessages } from "@/hooks/use-pending-messages";
import { useStreamConversation } from "@/hooks/use-stream-conversation";
import { useStuckConversationDetection } from "@/hooks/use-stuck-conversation-detection";
import { useSendInteractiveMessage } from "@/hooks/use-send-interactive-message";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { agentExecutionName } from "@/utils/agent-executions";
import { ToggleDetailsPanelKeyBinding } from "@/utils/platform";
import { IconSidebarSimpleRightSquare } from "central-icons/IconSidebarSimpleRightSquare";
import { useEffect, useMemo, useRef, type FC } from "react";
import { extractSpecFilePath } from "@/components/ai/pending-interactive-utils";

const SidebarIconClosed = withGitpodSize(IconSidebarSimpleRightSquare);

export type EnvironmentAgentExecutionConversationRef = {
    switchToReviewTab: () => void;
    setPendingSelection: () => void;
};

export type EnvironmentAgentExecutionConversationProps = {
    agentExecution: PlainAgentExecution | null;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    followMode?: FollowModeState;
    environmentId?: string;
    onBuildButtonAppear?: (specFilePath: string | null) => void;
};

export const EnvironmentAgentExecutionConversation: FC<EnvironmentAgentExecutionConversationProps> = ({
    agentExecution,
    isDetailsPanelOpen,
    onToggleDetailsPanel,
    followMode,
    environmentId,
    onBuildButtonAppear,
}) => {
    const { pendingMessages, handleNewMessage } = useOptimisticPendingMessages();
    const { error, messages, loadingMessage, lastAgentBlockContext } = useStreamConversation(
        agentExecution?.id,
        agentExecution?.status?.conversationUrl,
        pendingMessages,
    );
    useStuckConversationDetection(agentExecution, lastAgentBlockContext);

    useDetailsPanelToggleShortcut(() => {
        onToggleDetailsPanel();
    });

    const handleSendMessage = useSendInteractiveMessage(agentExecution?.id, handleNewMessage, messages);

    const pendingInteractiveBlock = useMemo(() => extractPendingInteractiveBlock(messages), [messages]);

    // Lives here (not in FragmentNextStepsProposal) because PendingInteractiveBlock
    // unmounts/remounts on parent re-renders, killing any internal timeouts.
    // Retries on each messages update because specFilePath may stream in after the proposal.
    const hasNotifiedTabSwitch = useRef(false);
    const hasNotifiedSpecFile = useRef(false);
    const hasPrimaryNextStep = pendingInteractiveBlock?.type === "nextStepsProposal"
        && pendingInteractiveBlock.actions.some((a) => a.variant === "primary");

    useEffect(() => {
        if (!hasPrimaryNextStep) return;

        if (!hasNotifiedTabSwitch.current) {
            hasNotifiedTabSwitch.current = true;
            const specFilePath = extractSpecFilePath(messages);
            onBuildButtonAppear?.(specFilePath);
            if (specFilePath) {
                hasNotifiedSpecFile.current = true;
            }
            return;
        }

        if (!hasNotifiedSpecFile.current) {
            const specFilePath = extractSpecFilePath(messages);
            if (specFilePath) {
                hasNotifiedSpecFile.current = true;
                onBuildButtonAppear?.(specFilePath);
            }
        }
    }, [hasPrimaryNextStep, messages, onBuildButtonAppear]);

    useEffect(() => {
        if (!hasPrimaryNextStep) {
            hasNotifiedTabSwitch.current = false;
            hasNotifiedSpecFile.current = false;
        }
    }, [hasPrimaryNextStep]);

    return (
        <div
            data-testid="environment-agent-execution-conversation"
            className={cn("flex h-full min-w-[100px] flex-col rounded-l-lg border-border-base bg-surface-01")}
        >
            <Header
                agentExecution={agentExecution}
                isDetailsPanelOpen={isDetailsPanelOpen}
                onToggleDetailsPanel={onToggleDetailsPanel}
            />
            <ConversationError error={error} loadingMessage={loadingMessage} />
            <ConversationContent
                messages={messages}
                agentExecution={agentExecution}
                environmentId={environmentId}
                onSendMessage={handleSendMessage}
                pendingInteractiveBlock={pendingInteractiveBlock}
            />
            <ConversationInput
                hasMessages={messages.length > 0}
                agentExecution={agentExecution}
                onNewMessage={(message) => handleNewMessage(message, messages)}
                followMode={followMode}
                messages={messages}
                environmentId={environmentId}
                pendingInteractiveBlock={
                    <PendingInteractiveBlock 
                        fragment={pendingInteractiveBlock} 
                        onSendMessage={handleSendMessage}
                        environmentId={environmentId}
                    />
                }
                pendingInteractiveBlockType={pendingInteractiveBlock?.type}
            />
        </div>
    );
};

const Header: FC<{
    agentExecution: PlainAgentExecution | null;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
}> = ({ agentExecution, isDetailsPanelOpen, onToggleDetailsPanel }) => {
    const displayName = useMemo(
        () => (agentExecution ? agentExecutionName(agentExecution) : "Conversation"),
        [agentExecution],
    );
    const { isMobileViewport: isMobile } = useIsMobileViewport();

    return (
        <div
            className={cn(
                "flex h-12 w-full items-center justify-between gap-2 border-b border-border-subtle px-4 py-2",
            )}
        >
            <div className="flex h-6 min-w-0 flex-1 items-center gap-0.5">
                {agentExecution ? (
                    <AgentExecutionDropdownMenu agentExecution={agentExecution} />
                ) : (
                    <div className="flex min-w-0 items-center gap-1 rounded-md p-1">
                        <span className="min-w-0 truncate text-left text-base font-bold">{displayName}</span>
                    </div>
                )}
            </div>
            {!isDetailsPanelOpen && (
                <div className="flex items-center justify-end gap-1">
                    {isMobile ? (
                        <Tooltip content="Show environment details">
                            <Button
                                variant="ghost"
                                className="rounded-[4px] border-0 p-0"
                                size="lg"
                                onClick={onToggleDetailsPanel}
                                LeadingIcon={IconPanelRightOpen}
                                aria-label="Show environment details"
                                data-tracking-id="show-environment-details-agent-execution-conversation"
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip
                            content={
                                <div className="flex flex-row items-center gap-2">
                                    <span>Show details panel</span>
                                    <KeyboardCombo keys={ToggleDetailsPanelKeyBinding} />
                                </div>
                            }
                        >
                            <Button
                                variant="ghost"
                                className="rounded-[4px] border-0 p-2"
                                size="sm"
                                onClick={onToggleDetailsPanel}
                                aria-label="Show right panel"
                                data-tracking-id="toggle-details-panel-agent-execution-conversation"
                            >
                                <SidebarIconClosed size="sm" />
                            </Button>
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    );
};
